import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, 
  Save,
  Loader2,
  Package,
  Container,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { BulkPricingPanel } from '../../components/admin/BulkPricingPanel';
import { DemurragePanel } from '../../components/admin/DemurragePanel';

const DEFAULT_DND = {
  demurrage_free_days: 5, demurrage_tier1_days: 5,
  demurrage_tier1: { '20ft': 75, '40ft': 150, '40ft_hc': 150, '20ft_reefer': 200, '40ft_reefer': 200 },
  demurrage_tier2: { '20ft': 150, '40ft': 300, '40ft_hc': 300, '20ft_reefer': 400, '40ft_reefer': 400 },
  detention_free_days: 7,
  detention_daily: { '20ft': 60, '40ft': 120, '40ft_hc': 120, '20ft_reefer': 180, '40ft_reefer': 180 }
};
import { toast } from 'sonner';

function AdminPricing() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  const [containerPrices, setContainerPrices] = useState({
    '20ft': 1500,
    '40ft': 2500,
    '40ft_hc': 2800,
    '20ft_reefer': 2500,
    '40ft_reefer': 4000
  });

  const [cargoMultipliers, setCargoMultipliers] = useState({
    'FCL': 1.0,
    'LCL': 0.6
  });

  const [surcharges, setSurcharges] = useState({
    fuel_surcharge_percent: 15,
    handling_fee_per_container: 150,
    documentation_fee: 75,
    security_fee: 50
  });
  const [bulkRates, setBulkRates] = useState({});
  const [bulkSurcharges, setBulkSurcharges] = useState({ fuel_surcharge_percent: 12, port_charges_per_ton: 4.5, stevedoring_per_ton: 6, minimum_freight: 25000 });
  const [dnd, setDnd] = useState(DEFAULT_DND);

  const containerTypeLabels = {
    '20ft': '20ft Standard',
    '40ft': '40ft Standard',
    '40ft_hc': '40ft High Cube',
    '20ft_reefer': '20ft Reefer',
    '40ft_reefer': '40ft Reefer'
  };

  const cargoTypeLabels = {
    'FCL': 'Full Container Load',
    'LCL': 'Less Container Load'
  };

  useEffect(function() {
    loadPricingConfig();
  }, []);

  async function loadPricingConfig() {
    setLoading(true);
    try {
      const response = await api.get('/admin/pricing');
      const config = response.data;
      if (config.container_prices) setContainerPrices(config.container_prices);
      if (config.cargo_multipliers) setCargoMultipliers(config.cargo_multipliers);
      if (config.surcharges) setSurcharges(config.surcharges);
      if (config.bulk_rates_per_ton) setBulkRates(config.bulk_rates_per_ton);
      if (config.bulk_surcharges) setBulkSurcharges(config.bulk_surcharges);
      if (config.demurrage_detention) setDnd({ ...DEFAULT_DND, ...config.demurrage_detention });
    } catch (err) {
      console.error('Failed to load pricing config:', err);
      toast.error('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  }

  function handleContainerPriceChange(type, value) {
    setContainerPrices(function(prev) {
      return { ...prev, [type]: parseFloat(value) || 0 };
    });
  }

  function handleMultiplierChange(type, value) {
    setCargoMultipliers(function(prev) {
      return { ...prev, [type]: parseFloat(value) || 0 };
    });
  }

  function handleSurchargeChange(type, value) {
    setSurcharges(function(prev) {
      return { ...prev, [type]: parseFloat(value) || 0 };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.put('/admin/pricing', {
        container_prices: containerPrices,
        cargo_multipliers: cargoMultipliers,
        surcharges: surcharges,
        bulk_rates_per_ton: bulkRates,
        bulk_surcharges: bulkSurcharges,
        demurrage_detention: dnd
      });
      setLastSaved(new Date().toLocaleTimeString());
      toast.success('Pricing configuration saved successfully');
    } catch (err) {
      toast.error('Failed to save pricing configuration');
    } finally {
      setSaving(false);
    }
  }

  function calculateSamplePrice() {
    const base = containerPrices['40ft'] * 2 * cargoMultipliers['FCL'];
    const fuelSurcharge = base * (surcharges.fuel_surcharge_percent / 100);
    const handling = surcharges.handling_fee_per_container * 2;
    const docs = surcharges.documentation_fee;
    const security = surcharges.security_fee;
    return {
      base,
      fuelSurcharge,
      handling,
      docs,
      security,
      total: base + fuelSurcharge + handling + docs + security
    };
  }

  const samplePrice = calculateSamplePrice();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Pricing</h1>
          <p className="text-gray-600 mt-1">Configure shipping rates and surcharges</p>
        </div>
        <div className="flex items-center gap-3">
          {lastSaved && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Saved at {lastSaved}
            </span>
          )}
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={loadPricingConfig}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button 
            className="bg-ink hover:bg-ink-800 text-white rounded-full"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-green-50 border border-green-200 rounded-sm p-4 flex items-start gap-4">
        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-green-800">Pricing configuration</p>
          <p className="text-sm text-green-600">
            These settings are saved to the database and affect how quotes and booking prices are calculated.
            Changes will apply to all new bookings.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Container Base Prices */}
        <div className="bg-white rounded-sm shadow-card p-6">
          <h2 className="text-xl font-medium tracking-[-0.01em] text-ink mb-6 flex items-center gap-2">
            <Container className="h-5 w-5 text-blue-600" />
            Container Base Prices
          </h2>
          <div className="space-y-4">
            {Object.entries(containerPrices).map(function([type, price]) {
              return (
                <div key={type} className="flex items-center justify-between gap-4">
                  <Label className="text-gray-700 font-medium w-40">{containerTypeLabels[type]}</Label>
                  <div className="relative flex-1 max-w-[180px]">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      min="0"
                      step="50"
                      value={price}
                      onChange={function(e) { handleContainerPriceChange(type, e.target.value); }}
                      className="pl-9 h-12 rounded-sm"
                      data-testid={"price-" + type}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cargo Type Multipliers */}
        <div className="bg-white rounded-sm shadow-card p-6">
          <h2 className="text-xl font-medium tracking-[-0.01em] text-ink mb-6 flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Cargo Type Multipliers
          </h2>
          <div className="space-y-4">
            {Object.entries(cargoMultipliers).map(function([type, multiplier]) {
              return (
                <div key={type} className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-sm">
                  <div>
                    <Label className="text-gray-700 font-medium">{cargoTypeLabels[type]}</Label>
                    <p className="text-xs text-gray-500 mt-1">
                      {type === 'FCL' ? 'Full container booking' : 'Shared container space'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={multiplier}
                      onChange={function(e) { handleMultiplierChange(type, e.target.value); }}
                      className="w-24 h-12 rounded-sm text-center"
                      data-testid={"multiplier-" + type}
                    />
                    <span className="text-gray-500">×</span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Multiplier is applied to the base container price. 1.0 = full price, 0.6 = 60% of price.
          </p>
        </div>

        {/* Surcharges */}
        <div className="bg-white rounded-sm shadow-card p-6">
          <h2 className="text-xl font-medium tracking-[-0.01em] text-ink mb-6 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Surcharges & Fees
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-sm">
              <div>
                <Label className="text-gray-700 font-medium">Fuel surcharge</Label>
                <p className="text-xs text-gray-500 mt-1">Percentage of base price</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={surcharges.fuel_surcharge_percent}
                  onChange={function(e) { handleSurchargeChange('fuel_surcharge_percent', e.target.value); }}
                  className="w-24 h-12 rounded-sm text-center"
                  data-testid="fuel-surcharge"
                />
                <span className="text-gray-500">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-sm">
              <div>
                <Label className="text-gray-700 font-medium">Handling fee</Label>
                <p className="text-xs text-gray-500 mt-1">Per container</p>
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  step="10"
                  value={surcharges.handling_fee_per_container}
                  onChange={function(e) { handleSurchargeChange('handling_fee_per_container', e.target.value); }}
                  className="w-32 pl-9 h-12 rounded-sm"
                  data-testid="handling-fee"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-sm">
              <div>
                <Label className="text-gray-700 font-medium">Documentation fee</Label>
                <p className="text-xs text-gray-500 mt-1">Per booking</p>
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  step="5"
                  value={surcharges.documentation_fee}
                  onChange={function(e) { handleSurchargeChange('documentation_fee', e.target.value); }}
                  className="w-32 pl-9 h-12 rounded-sm"
                  data-testid="doc-fee"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-sm">
              <div>
                <Label className="text-gray-700 font-medium">Security fee</Label>
                <p className="text-xs text-gray-500 mt-1">Per booking</p>
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  step="5"
                  value={surcharges.security_fee}
                  onChange={function(e) { handleSurchargeChange('security_fee', e.target.value); }}
                  className="w-32 pl-9 h-12 rounded-sm"
                  data-testid="security-fee"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Price Calculator Preview */}
        <div className="bg-gradient-to-br from-[#0F1B26] to-[#0F1B26] rounded-sm shadow-card p-6 text-white">
          <h2 className="text-xl font-medium tracking-[-0.01em] mb-6 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-blue-600" />
            Price Calculator Preview
          </h2>
          <p className="text-white/70 text-sm mb-4">
            Sample calculation for 2× 40ft Standard containers (FCL)
          </p>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Base Price (2 × ${containerPrices['40ft']})</span>
              <span className="font-medium">{formatCurrency(samplePrice.base)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Fuel Surcharge ({surcharges.fuel_surcharge_percent}%)</span>
              <span className="font-medium">{formatCurrency(samplePrice.fuelSurcharge)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Handling Fee (2 × ${surcharges.handling_fee_per_container})</span>
              <span className="font-medium">{formatCurrency(samplePrice.handling)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Documentation fee</span>
              <span className="font-medium">{formatCurrency(samplePrice.docs)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Security fee</span>
              <span className="font-medium">{formatCurrency(samplePrice.security)}</span>
            </div>
            <div className="border-t border-white/20 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">Total quote</span>
                <span className="text-[32px] sv-display text-blue-600">{formatCurrency(samplePrice.total)}</span>
              </div>
            </div>
          </div>
        </div>

        <BulkPricingPanel
          rates={bulkRates}
          surcharges={bulkSurcharges}
          onRateChange={function(k, v) { setBulkRates(function(p) { return { ...p, [k]: parseFloat(v) || 0 }; }); }}
          onSurchargeChange={function(k, v) { setBulkSurcharges(function(p) { return { ...p, [k]: parseFloat(v) || 0 }; }); }}
        />
        <DemurragePanel dnd={dnd} onChange={setDnd} />
      </div>
    </div>
  );
}

export default AdminPricing;
